import { useState, useCallback } from "react";
import usersApi from "../API/usersApi";

export const useUsersApi = (showNotificationArg) => {
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const [user, setUser] = useState(null);
  const [chartData, setChartData] = useState(null);

  // Use passed showNotification or fallback to local (for backward compatibility)
  let showNotification = showNotificationArg;
  if (!showNotification) {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    showNotification = require("./useNotification").default().showNotification;
  }

  // Get all users
  const fetchUsers = useCallback(
    async (isActive = true) => {
      setLoading(true);
      try {
        const response = await usersApi.getAll(isActive);
        if (response.error) {
          showNotification(response.error, "error");
          setUsers([]);
          return { success: false, error: response.error };
        }
        setUsers(response.data || response || []);
        return { success: true, data: response.data || response };
      } catch (error) {
        console.error("Error fetching users:", error);
        showNotification("Error fetching users", "error");
        return {
          success: false,
          error: error.response?.data?.message || "Failed to fetch users",
        };
      } finally {
        setLoading(false);
      }
    },
    [showNotification]
  );

  // Get user by ID
  const fetchUserById = useCallback(
    async (id) => {
      setLoading(true);
      try {
        const response = await usersApi.getById(id);
        setUser(response.data);
        return { success: true, data: response.data };
      } catch (error) {
        console.error("Error fetching user details:", error);
        showNotification("Error fetching user details", "error");
        return {
          success: false,
          error:
            error.response?.data?.message || "Failed to fetch user details",
        };
      } finally {
        setLoading(false);
      }
    },
    [showNotification]
  );

  // Create new user
  const createInitialUser = useCallback(
    async (userData) => {
      setLoading(true);
      try {
        const response = await usersApi.createInitial(userData);
        if (response.error) {
          showNotification(response.error, "error");
          return { success: false, error: response.error };
        }
        showNotification("User created successfully", "success");
        return { success: true, data: response.data };
      } catch (error) {
        console.error("Error creating user:", error);
        showNotification(
          error.response?.data?.message || "Failed to create user",
          "error"
        );
        return {
          success: false,
          error: error.response?.data?.message || "Failed to create user",
        };
      } finally {
        setLoading(false);
      }
    },
    [showNotification]
  );

  // Update user
  const updateUser = useCallback(
    async (id, userData) => {
      setLoading(true);
      try {
        const response = await usersApi.update(id, userData);
        if (response.error) {
          showNotification(response.error, "error");
          return { success: false, error: response.error };
        }
        showNotification("User updated successfully", "success");
        return { success: true };
      } catch (error) {
        console.error("Error updating user:", error);
        showNotification(
          error.response?.data?.message || "Failed to update user",
          "error"
        );
        return {
          success: false,
          error: error.response?.data?.message || "Failed to update user",
        };
      } finally {
        setLoading(false);
      }
    },
    [showNotification]
  );

  // Delete user
  const deleteUser = useCallback(
    async (id) => {
      setLoading(true);
      try {
        const response = await usersApi.delete(id);
        if (response.error) {
          showNotification(response.error, "error");
          return { success: false, error: response.error };
        }
        showNotification("User deleted successfully", "success");
        return { success: true };
      } catch (error) {
        console.error("Error deleting user:", error);
        showNotification(
          error.response?.data?.message || "Failed to delete user",
          "error"
        );
        return {
          success: false,
          error: error.response?.data?.message || "Failed to delete user",
        };
      } finally {
        setLoading(false);
      }
    },
    [showNotification]
  );

  // Get chart data
  const fetchChartData = useCallback(
    async (period = "7days", startDate, endDate) => {
      setLoading(true);
      try {
        const response = await usersApi.getChartData(
          period,
          startDate,
          endDate
        );
        if (response.error) {
          showNotification(response.error, "error");
          return { success: false, error: response.error };
        }
        setChartData(response.data);
        return { success: true, data: response.data };
      } catch (error) {
        console.error("Error fetching chart data:", error);
        showNotification(
          error.response?.data?.message || "Failed to fetch chart data",
          "error"
        );
        return {
          success: false,
          error: error.response?.data?.message || "Failed to fetch chart data",
        };
      } finally {
        setLoading(false);
      }
    },
    [showNotification]
  );

  return {
    loading,
    users,
    user,
    chartData,
    fetchUsers,
    fetchUserById,
    createInitialUser,
    updateUser,
    deleteUser,
    fetchChartData,
  };
};

export default useUsersApi;
